import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';
import { Calendar, MapPin, Images as ImagesIcon, X } from 'lucide-react';

export default function VolunteerGalleryPage() {
    const [albums, setAlbums] = useState([]);
    const [openAlbum, setOpenAlbum] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    useSEO({
        title: 'Volunteer Photo Gallery',
        description:
            'Volunteer Photo Gallery — event-wise albums from VVVSI Trust programs across Tamil Nadu and Puducherry. See our 3,400+ volunteers in action across employment, education, health, environment and disaster relief drives.',
    });

    useEffect(() => {
        publicApi.getVolunteerAlbums().then(setAlbums).catch(() => {});
    }, []);

    return (
        <div data-testid="volunteer-gallery-page">
            <PageHero
                eyebrow="Volunteer Photo Gallery"
                title="Our 3,400+ volunteers, on the ground."
                subtitle="Event-wise albums from placement drives, health camps, career yatras, disaster relief and environmental initiatives."
            />

            <section className="section-pad">
                <div className="container-page">
                    {albums.length === 0 ? (
                        <div className="text-center text-muted-foreground py-16">
                            No albums yet — check back soon.
                        </div>
                    ) : (
                        <div
                            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
                            data-testid="volunteer-albums-grid"
                        >
                            {albums.map((a, i) => (
                                <motion.button
                                    key={a.id}
                                    type="button"
                                    onClick={() => setOpenAlbum(a)}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    className="text-left"
                                    data-testid={`album-card-${a.id}`}
                                >
                                    <Card className="h-full bg-card border-border card-lift overflow-hidden">
                                        <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                                            {a.cover_image ? (
                                                <img
                                                    src={a.cover_image}
                                                    alt={a.event_name}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : null}
                                            <div className="absolute top-3 right-3">
                                                <Badge
                                                    variant="secondary"
                                                    className="bg-white/90 text-[hsl(var(--primary))] backdrop-blur-sm font-semibold"
                                                >
                                                    <ImagesIcon className="h-3 w-3 mr-1" />
                                                    {(a.photos || []).length} photos
                                                </Badge>
                                            </div>
                                        </div>
                                        <CardContent className="p-5">
                                            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-muted-foreground">
                                                {a.event_date && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        {a.event_date}
                                                    </span>
                                                )}
                                                {a.location && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <MapPin className="h-3 w-3" />
                                                        {a.location}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="font-display font-semibold text-lg mb-1 leading-snug">
                                                {a.event_name}
                                            </div>
                                            {a.description && (
                                                <div className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                                                    {a.description}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Album Dialog */}
            <Dialog
                open={!!openAlbum}
                onOpenChange={(o) => {
                    if (!o) {
                        setOpenAlbum(null);
                        setLightbox(null);
                    }
                }}
            >
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {openAlbum?.event_name}
                        </DialogTitle>
                        <DialogDescription>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                                {openAlbum?.event_date && (
                                    <span className="inline-flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {openAlbum.event_date}
                                    </span>
                                )}
                                {openAlbum?.location && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {openAlbum.location}
                                    </span>
                                )}
                            </div>
                            {openAlbum?.description && (
                                <p className="mt-2 text-sm text-foreground/80">
                                    {openAlbum.description}
                                </p>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2"
                        data-testid="album-photos-grid"
                    >
                        {(openAlbum?.photos || []).map((url, idx) => (
                            <button
                                key={`${url}-${idx}`}
                                type="button"
                                onClick={() => setLightbox(url)}
                                className="aspect-square bg-muted rounded-md overflow-hidden group"
                                data-testid={`album-photo-${idx}`}
                            >
                                <img
                                    src={url}
                                    alt=""
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                />
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Photo Lightbox */}
            <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
                <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Photo</DialogTitle>
                    </DialogHeader>
                    <button
                        type="button"
                        onClick={() => setLightbox(null)}
                        className="absolute top-2 right-2 h-9 w-9 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center z-10"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-center justify-center min-h-[60vh]">
                        {lightbox && (
                            <img
                                src={lightbox}
                                alt=""
                                className="max-h-[85vh] max-w-full object-contain"
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
